import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  main: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  single: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 5,
    paddingVertical: 7,
  },
  textDesign: {
    fontSize: 11,
    fontFamily: 'Serif',
  },
  textDesignGrand: {
    fontSize: 12,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    color: 'darkslateblue',
    fontFamily: 'Serif',
  },
  break: {
    flexDirection: 'row',
    padding: 0.4,
    backgroundColor: 'slategray',
    marginTop: 0,
  },
});

const getTestItemsPrice = (array) => {
  const price = array.reduce(
    (total, item) => total + parseInt(item.purchasable_order_item.sell_price),
    0
  );

  return parseInt(price);
};

const getPatientMaterialFee = (patient, order) => {
  const items = (patient && patient.testItem) || [];
  const own = items.find((item) => item && item.meterial_fee != null);
  if (own) {
    return parseInt(own.meterial_fee) || 0;
  }
  return order.orderitem.length !== 0
    ? parseInt(order.orderitem[0].meterial_fee) || 0
    : 0;
};

// A test is discountable unless its DiagnosticTest.is_discountable flag is explicitly false.
// Checks both JSON shapes the API returns a test under (single test vs package-style item).
const isOrderItemDiscountable = (item) => {
  const poi = (item && item.purchasable_order_item) || {};
  const single = poi.testitem_purchasable_oi;
  const dt1 = single && typeof single === 'object' ? single.diagnostic_test : null;
  if (dt1 && typeof dt1.is_discountable === 'boolean') {
    return dt1.is_discountable;
  }
  const orderArr = poi.testitem_purchasable_order;
  const first = Array.isArray(orderArr) && orderArr.length ? orderArr[0] : null;
  const dt2 = first && first.diagnostic_test;
  if (dt2 && typeof dt2.is_discountable === 'boolean') {
    return dt2.is_discountable;
  }
  return true;
};

// Sum of sell_price for only the discountable items in the list (e.g. excludes CBC).
const getDiscountableTestsPrice = (items = []) => {
  const price = items.reduce((total, item) => {
    if (!item || !item.purchasable_order_item) return total;
    if (isOrderItemDiscountable(item) === false) return total;
    return total + parseInt(item.purchasable_order_item.sell_price);
  }, 0);
  return parseInt(price) || 0;
};

export default function PriceSection({
  order,
  materialPerson,
  discountPrice,
  patient,
  i,
  discountPercentage,
}) {
  const patientMaterialFee = getPatientMaterialFee(patient, order);
  // Discount only applies to this patient's discountable tests (e.g. excludes CBC).
  const patientDiscountableFee = getDiscountableTestsPrice(patient.testItem || []);

  return (
    <View style={styles.main}>
      <View style={{ flex: 1 }}></View>
      <View style={{ flex: 1 }}>
        <Single
          title='Total Service Price'
          price={getTestItemsPrice(patient.testItem)}
        />
        {i === 0 && (
          <>
            <View style={styles.break}></View>
            <Single
              title={`Pick up charges`}
              price={parseInt(order.collection_fee)}
            />
          </>
        )}
        <View style={styles.break}></View>
        <Single title={`Material fee`} price={patientMaterialFee} />
        {discountPercentage && (
          <>
            <View style={styles.break}></View>
            <Single
              title='Discount'
              price={Math.round(
                (discountPercentage / 100) * patientDiscountableFee
              )}
            />
          </>
        )}

        <View
          style={[styles.break, { backgroundColor: 'darkslateblue' }]}
        ></View>
        <View style={styles.single}>
          <Text style={styles.textDesignGrand}>Grand Total</Text>
          <Text style={styles.textDesignGrand}>
            {i === 0 &&
              (discountPercentage
                ? getTestItemsPrice(patient.testItem) +
                  patientMaterialFee +
                  parseInt(order.collection_fee) -
                  (discountPercentage / 100) * patientDiscountableFee
                : getTestItemsPrice(patient.testItem) +
                  patientMaterialFee +
                  parseInt(order.collection_fee))}
            {i !== 0 &&
              (discountPercentage
                ? getTestItemsPrice(patient.testItem) +
                  patientMaterialFee -
                  (discountPercentage / 100) * patientDiscountableFee
                : getTestItemsPrice(patient.testItem) + patientMaterialFee)}
            Tk
          </Text>
        </View>
      </View>
    </View>
  );
}

const Single = ({ title, price }) => (
  <View style={styles.single}>
    <Text style={styles.textDesign}>{title}</Text>
    <Text style={styles.textDesign}>{price} Tk</Text>
  </View>
);
